<template>
  <div class="c-chat-input">
    <section class="input">
      <c-form-builder
        ref="form"
        no-actions
        no-bottom-shadow
        no-upper-shadow
        :key="formId"
        :schema="schema"
        :errors="errors"
        :disabled="disabledSubmit"
        v-model="model"
        show-overflow
        @keydown.native.enter="handleEnter"
        @submit="submit"
        data-testid="c-chat-form-builder"
      >
        <template #message-under-input>
          <div v-if="hasFiles" class="c-chat-input-files" data-testid="c-chat-files-container">
            <c-info-tag
              v-for="(file, index) in files"
              :key="file.id"
              class="file"
              removable
              icon="clip-attachment"
              icon-size="18"
              icon-gap="2"
              @remove="$emit('attachment:remove', { ...file, index })"
              data-testid="c-chat-file-tag"
            >
              {{ file.name }}
            </c-info-tag>
          </div>
        </template>
      </c-form-builder>
    </section>

    <c-button
      flat
      class="action attachment"
      icon="clip-attachment"
      icon-size="24"
      :disabled="disabledAttachment || loading"
      data-testid="c-chat-attachment-button"
      @click="openAttachmentInput"
    />

    <c-button
      data-testid="c-chat-submit-button"
      class="action submit"
      icon="send-message"
      icon-size="24"
      :disabled="disabledSubmit"
      :loading="loading"
      @click="triggerForm"
    />

    <input
      style="display: none"
      ref="attachmentInput"
      type="file"
      :accept="extensions"
      :multiple="multiple"
      data-testid="c-chat-attachment-input"
      @change.prevent="onAddAttachments"
    >
  </div>
</template>

<script>
import { MediaQuery } from '@convenia/mixins'

export default {
  name: 'CChatInput',

  mixins: [ MediaQuery ],

  props: {
    /**
     * Input's placeholder
     */
    placeholder: {
      type: String,
      default: 'Digite sua mensagem',
    },

    /**
     * Input's message text value
     */
    value: {
      type: String,
      default: '',
    },

    /**
     * Accepted file extensions
     *
     * @type {string[]}
     */
    acceptedFileExtensions: {
      type: Array,
      default: () => [ '*' ],
    },

    /**
     * Whether the input should accept multiple files
     */
    multiple: {
      type: Boolean,
      default: true,
    },

    /**
     * Input errors
     */
    errors: {
      type: Object,
      default: () => ({}),
    },

    /**
     * Whether the attachment button is disabled
     */
    disabledAttachment: {
      type: Boolean,
      default: false,
    },

    /**
     * Max of files the chat can suport
     */
    maxAttachmentQuantity: {
      type: Number,
      default: 10,
    },

    /**
     * Max of characters the user can text
     */
    maxLength: {
      type: Number,
      default: null,
    },

    /**
     * Whether the submit button is disabled
     */
    disabledSubmit: {
      type: Boolean,
      default: false,
    },

    /**
     * Whether the submit button is loading
     */
    loading: {
      type: Boolean,
      default: false,
    },

    /**
     * An array of files to be added to the conversation.
     *
     * @typedef {Object} Item
     * @property {string} id - The unique identifier for the item.
     * @property {string} name - The name of the item.
     *
     * @type {Item[]}
    */
    files: {
      type: Array,
      default: () => [],
    },

    /**
     * Whether the message field is required
     */
    isMessageRequired: {
      type: Boolean,
      default: true,
    },

    /**
     * Whether the message field gets not required when there are files
     */
    isMessageRequiredWhenFiles: {
      type: Boolean,
      default: false,
    },
  },

  data () {
    return {
      formId: 0,
    }
  },

  watch: {
    hasFiles (value) {
      !this.isMessageRequiredWhenFiles && value && this.$refs.form.triggerFieldValidation('message')
    },

    hasFilesError (value) {
      !value && this.$refs.form.triggerFieldValidation('message')
    },

    files: {
      handler () {
        if (this.$refs.attachmentInput) {
          this.$refs.attachmentInput.value = ''
        }
      },
      deep: true
    },

    value () {
      setTimeout(() => {
        this.$refs.form.hideValidations()
      }, 500)
    },
  },

  emits: [
    /**
     * Emits the value when the users inputs it on the input
     */
    'input',

    /**
     * Emits the input value when the user presses the enter key
     * or clicks the submit button
     */
    'submit',

    /**
     * Emits the files when the user adds attachments
     */
    'attachments:add',

    /**
     * Emits the file to be removed
     */
    'attachment:remove',

    /**
     * Emits the event to show quantity error
     */
    'attachments:error',
  ],

  computed: {
    extensions () {
      return this.acceptedFileExtensions.join(',')
    },

    rows () {
      return this.isMobile ? 1 : 3
    },

    hasFiles () {
      return !!this.files?.length
    },

    model: {
      get () {
        return {
          message: this.value
        }
      },

      set ({ message }) {
        this.$emit('input', message)
      },
    },

    hasFilesError () {
      return this.maxAttachmentQuantity
        && this.files.length > this.maxAttachmentQuantity
    },

    schema () {
      const isRequired = this.hasFilesError || (
        this.isMessageRequiredWhenFiles
          ? this.isMessageRequired
          : !this.hasFiles
      )

      const getValidationMessage = () => {
        if (!isRequired) return false
        return this.hasFilesError
          ? `Você atingiu o limite de ${this.maxAttachmentQuantity} arquivos`
          : 'O campo é obrigatório'
      }

      return {
        message: {
          type: 'text',
          'text-area': true,
          placeholder: this.placeholder,
          rows: this.rows,
          disabled: this.loading,
          maxLength: this.maxLength,
          required: isRequired,
          validation: {
            required: getValidationMessage(),
            custom: () => !this.hasFilesError || `Você atingiu o limite de ${this.maxAttachmentQuantity} arquivos`
          }
        }
      }
    },
  },

  methods: {
    openAttachmentInput () {
      this.$refs.attachmentInput && this.$refs.attachmentInput.click()
    },

    submit () {
      this.$emit('submit', this.model.message, this.files)
      this.formId += 1
    },

    triggerForm () {
      this.$refs.form.onSubmit()
    },

    handleEnter (event) {
      if (event?.shiftKey) return

      event?.preventDefault()
      this.triggerForm()
    },

    onAddAttachments (event) {
      const { files } = event?.target || {}

      if (
        this.maxAttachmentQuantity
        && (this.files.length + files.length) > this.maxAttachmentQuantity
      ) { this.$emit('attachments:error') }

      this.$emit('attachments:add', files || [])
      this.$refs.attachmentInput.value = ''
    },
  },
}
</script>

<style lang="scss">
.c-chat-input {
  display: flex;
  gap: 10px;
  align-items: center;
  width: 100%;

  & > .input {
    flex: 1 1;
    position: relative;

    & > .c-form-builder > .c-shadowed > .wrapper > .form-fields {
      padding: 0px;
      width: 100%;

      & > .field {
        grid-template-columns: 1fr;
        max-width: none;
      }
    }
  }

  & > .action {
    flex: 0 0;
    margin-bottom: 0;
    transition: margin-bottom 0.3s ease;
  }

  &:has(.c-form-builder > .c-shadowed > .wrapper > .form-fields > .field > .validation > .wrapper.-show) {
    & > .action { margin-bottom: 24px; }
  }

  @include media-after(map-get($breakpoints, tablet)) {
    & > .action.attachment { order: -1; }
  }

  & .c-chat-input-files {
    padding: 5px 10px 10px;
    display: flex;
    flex-wrap: wrap;
    gap: 10px;

    & > .file { max-width: 130px; }
  }
}
</style>
